// Initialization Provider - owns all initialization state and orchestration
import SystemForceUpdate from 'features/forceUpdate/SystemForceUpdate';
import React, {
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { initializeAnalytics } from 'services/analytics/analyticsManager';
import { initializeDeeplinkHandling } from 'services/deeplinks/deeplinkManager';
import { initializeEngagement } from 'services/engagement/engagementManager';
import {
  checkForceUpdate,
  ForceUpdateResult,
} from 'services/forceUpdate/forceUpdateManager';
import { initializeStatsig } from 'services/statsig/statsigManager';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import { EnvironmentContext } from 'shared/providers/environment';
import { getNetworkStateSnapshot } from 'shared/utils/network/networkState';
import { logBreadcrumb, logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

/**
 * Phases of app initialization
 * Executed sequentially to prevent race conditions
 */
export enum InitializationPhase {
  NOT_STARTED = 'NOT_STARTED',
  ANALYTICS = 'ANALYTICS', // Segment + AppsFlyer
  STATSIG = 'STATSIG',
  FORCE_UPDATE_CHECK = 'FORCE_UPDATE_CHECK',
  BLOCKED = 'BLOCKED', // Force update or service maintenance required - ONLY blocking phase
  DEEPLINKS = 'DEEPLINKS',
  ENGAGEMENT = 'ENGAGEMENT', // Braze + Push Notifications
  COMPLETED = 'COMPLETED',
}

interface InitializationState {
  phase: InitializationPhase;
  isInitialized: boolean;
  forceUpdateResult?: ForceUpdateResult;
}

export const InitializationProvider = ({ children }: PropsWithChildren) => {
  const { currentEnv } = useContext(EnvironmentContext);
  const [initializationState, setInitializationState] =
    useState<InitializationState>({
      phase: InitializationPhase.NOT_STARTED,
      isInitialized: false,
    });
  const initializationInProgress = useRef(false);

  const updatePhase = (phase: InitializationPhase) => {
    const network = getNetworkStateSnapshot();
    logBreadcrumb({
      level: 'info',
      message: `Initialization phase: ${phase}`,
      data: {
        phase,
        isConnected: network.isConnected,
        isInternetReachable: network.isInternetReachable,
        networkType: network.type,
      },
    });

    setInitializationState((prev) => ({
      ...prev,
      phase,
    }));
  };

  const performInitialization = async () => {
    logBreadcrumb({
      level: 'info',
      message: 'App initialization started',
    });

    // Phase 1: Analytics (Segment + AppsFlyer)
    updatePhase(InitializationPhase.ANALYTICS);

    try {
      await initializeAnalytics();
    } catch (error) {
      logErrorWithContext(error, {
        featureArea: FeatureArea.INITIALIZATION,
        responsibleTeam: ResponsibleTeam.FRONTEND_PLATFORM,
        severity: ErrorSeverity.HIGH,
        additionalContext: { phase: 'analytics' },
      });
    }

    // Phase 2: Statsig
    updatePhase(InitializationPhase.STATSIG);

    try {
      await initializeStatsig(currentEnv);
    } catch (error) {
      logErrorWithContext(error, {
        featureArea: FeatureArea.INITIALIZATION,
        responsibleTeam: ResponsibleTeam.FRONTEND_PLATFORM,
        severity: ErrorSeverity.HIGH,
        additionalContext: { phase: 'statsig' },
      });
    }

    // Phase 3: Force Update Check
    // This is the ONLY phase that can block the app (with safe fallback)
    updatePhase(InitializationPhase.FORCE_UPDATE_CHECK);

    try {
      const forceUpdateResult = await checkForceUpdate();

      if (forceUpdateResult.shouldBlock) {
        logBreadcrumb({
          level: 'info',
          message:
            'Force update or service maintenance required - blocking app',
          data: forceUpdateResult,
        });

        // Block initialization and set blocked state
        setInitializationState((prev) => ({
          ...prev,
          phase: InitializationPhase.BLOCKED,
          isInitialized: true,
          forceUpdateResult,
        }));

        return;
      }
    } catch (error) {
      logErrorWithContext(error, {
        featureArea: FeatureArea.INITIALIZATION,
        responsibleTeam: ResponsibleTeam.FRONTEND_PLATFORM,
        severity: ErrorSeverity.MEDIUM,
        additionalContext: { phase: 'force_update_check' },
      });
    }

    // Phase 4: Deeplinks
    updatePhase(InitializationPhase.DEEPLINKS);

    try {
      await initializeDeeplinkHandling();
    } catch (error) {
      logErrorWithContext(error, {
        featureArea: FeatureArea.INITIALIZATION,
        responsibleTeam: ResponsibleTeam.FRONTEND_PLATFORM,
        severity: ErrorSeverity.MEDIUM,
        additionalContext: { phase: 'deeplinks' },
      });
    }

    // Phase 5: Engagement (Braze + Push Notifications)
    updatePhase(InitializationPhase.ENGAGEMENT);

    try {
      await initializeEngagement();
    } catch (error) {
      logErrorWithContext(error, {
        featureArea: FeatureArea.INITIALIZATION,
        responsibleTeam: ResponsibleTeam.FRONTEND_PLATFORM,
        severity: ErrorSeverity.MEDIUM,
        additionalContext: { phase: 'engagement' },
      });
    }

    // Initialization complete
    updatePhase(InitializationPhase.COMPLETED);
    setInitializationState((prev) => ({ ...prev, isInitialized: true }));

    logBreadcrumb({
      level: 'info',
      message: 'App initialization completed successfully',
    });
  };

  useEffect(() => {
    if (initializationInProgress.current || initializationState.isInitialized) {
      return;
    }
    initializationInProgress.current = true;

    void (async () => {
      await performInitialization();
      initializationInProgress.current = false;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show loading screen until initialization completes
  if (!initializationState.isInitialized) {
    return <FullScreenLoading />;
  }

  // Show force update/service maintenance screen if blocked
  // This is the ONLY blocking state - all other failures are logged but allow app to continue
  if (initializationState.phase === InitializationPhase.BLOCKED) {
    return (
      <SystemForceUpdate
        forceUpdateResult={initializationState.forceUpdateResult!}
      />
    );
  }

  // Render children only after successful initialization (and not blocked)
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};
